import React, { createContext, useCallback, useContext, useMemo } from "react"

import { useMMKVString } from "react-native-mmkv"

const PENDING_CHANNEL_STORAGE_KEY = "channelLink.pendingChannelId"

interface ChannelLinkContextType {
  pendingChannelId: string | null
  setPendingChannelId: (id: string) => void
  clearPendingChannelId: () => void
}

const ChannelLinkContext = createContext<ChannelLinkContextType | null>(null)

export function ChannelLinkProvider({ children }: React.PropsWithChildren) {
  const [rawPending, setRawPending] = useMMKVString(PENDING_CHANNEL_STORAGE_KEY)

  const pendingChannelId = rawPending && rawPending.length > 0 ? rawPending : null

  const setPendingChannelId = useCallback(
    (id: string) => {
      const trimmed = id.trim()
      if (!trimmed) return
      setRawPending(trimmed)
    },
    [setRawPending],
  )

  const clearPendingChannelId = useCallback(() => {
    setRawPending(undefined)
  }, [setRawPending])

  const value = useMemo(
    () => ({
      pendingChannelId,
      setPendingChannelId,
      clearPendingChannelId,
    }),
    [pendingChannelId, setPendingChannelId, clearPendingChannelId],
  )

  return <ChannelLinkContext.Provider value={value}>{children}</ChannelLinkContext.Provider>
}

export function useChannelLink() {
  const ctx = useContext(ChannelLinkContext)
  if (!ctx) {
    throw new Error("useChannelLink must be used within ChannelLinkProvider")
  }
  return ctx
}
